import type { NotificationDto } from './types';

/**
 * Handles action=redirect notifications: marks the row as shown and navigates the browser
 * to redirect_url. No popup is shown. Used e.g. for "you were invited to a table".
 *
 * Defensively validates the redirect_url is a same-origin path or a recognized host before navigating —
 * we never want a notification to send a player to an arbitrary external URL.
 */
export class RedirectHandler {
  constructor(private readonly markShownEndpoint: string = '/notifications/shown') {}

  async handle(item: NotificationDto): Promise<void> {
    if (item.action !== 'redirect') return;
    if (!this.isSafeUrl(item.redirect_url)) {
      console.warn('[RedirectHandler] refused unsafe redirect_url:', item.redirect_url);
      return;
    }

    // Fire-and-forget mark as shown — we don't want to block the navigation on it.
    try {
      void fetch(this.markShownEndpoint + '?uuid=' + encodeURIComponent(item.uuid), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
      });
    } catch {
      // ignore — server-side cron will not retry because there's nothing to retry
    }

    window.location.assign(item.redirect_url);
  }

  private isSafeUrl(url: string): boolean {
    if (!url) return false;
    if (url.startsWith('/') && !url.startsWith('//')) return true; // same-origin path
    try {
      const u = new URL(url, window.location.origin);
      return u.origin === window.location.origin;
    } catch {
      return false;
    }
  }
}
